import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSave, FaTimes, FaImage, FaSort, FaChartBar, FaTrophy, FaBuilding
} from 'react-icons/fa';

const ContentBlocks = () => {
  const { can } = usePermission();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState('stats');

  const [formData, setFormData] = useState({
    // Stats fields
    label: '',
    value: '',
    icon: '',
    order_index: 0,
    // Milestones fields
    year: new Date().getFullYear(),
    title: '',
    description: '',
    // Client logos fields
    name: '',
    logo: null,
    url: '',
    active: true
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  const tabs = [
    { id: 'stats', label: 'Statistics', icon: <FaChartBar />, color: 'from-blue-500 to-blue-600' },
    { id: 'milestones', label: 'Milestones', icon: <FaTrophy />, color: 'from-green-500 to-green-600' },
    { id: 'client-logos', label: 'Client Logos', icon: <FaBuilding />, color: 'from-purple-500 to-purple-600' }
  ];

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      switch (activeTab) {
        case 'stats':
          await fetchStats();
          break;
        case 'milestones':
          await fetchMilestones();
          break;
        case 'client-logos':
          await fetchClientLogos();
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const response = await axios.get(`${SERVER}/content/admin/stats`, {
      withCredentials: true
    });
    setStats(response.data);
  };

  const fetchMilestones = async () => {
    const response = await axios.get(`${SERVER}/content/admin/milestones`, {
      withCredentials: true
    });
    setMilestones(response.data);
  };

  const fetchClientLogos = async () => {
    const response = await axios.get(`${SERVER}/content/admin/client-logos`, {
      withCredentials: true
    });
    setClientLogos(response.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingItem) return;
    if (!canEdit && editingItem) return;

    try {
      let response;
      const endpoint = modalType === 'client-logos' ? 'client-logos' : modalType;
      
      if (modalType === 'client-logos') {
        const formDataToSend = new FormData();
        Object.keys(formData).forEach(key => {
          if (key === 'logo' && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          } else if (key !== 'logo') {
            formDataToSend.append(key, formData[key]);
          }
        });

        if (editingItem) {
          response = await axios.put(`${SERVER}/content/admin/${endpoint}/${editingItem.id}`, formDataToSend, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          response = await axios.post(`${SERVER}/content/admin/${endpoint}`, formDataToSend, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      } else {
        if (editingItem) {
          response = await axios.put(`${SERVER}/content/admin/${endpoint}/${editingItem.id}`, formData, {
            withCredentials: true
          });
        } else {
          response = await axios.post(`${SERVER}/content/admin/${endpoint}`, formData, {
            withCredentials: true
          });
        }
      }

      if (response.status === 200 || response.status === 201) {
        fetchData();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleEdit = (item) => {
    if (!canEdit) return;
    
    setEditingItem(item);
    setModalType(activeTab);
    
    if (activeTab === 'stats') {
      setFormData({
        label: item.label || '',
        value: item.value || '',
        icon: item.icon || '',
        order_index: item.order_index || 0
      });
    } else if (activeTab === 'milestones') {
      setFormData({
        year: item.year || new Date().getFullYear(),
        title: item.title || '',
        description: item.description || '',
        order_index: item.order_index || 0
      });
    } else if (activeTab === 'client-logos') {
      setFormData({
        name: item.name || '',
        logo: null,
        url: item.url || '',
        order_index: item.order_index || 0,
        active: item.active
      });
    }
    
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!canDelete) return;
    
    const itemName = item.label || item.title || item.name;
    if (window.confirm(`Are you sure you want to delete "${itemName}"?`)) {
      try {
        const endpoint = activeTab === 'client-logos' ? 'client-logos' : activeTab;
        await axios.delete(`${SERVER}/content/admin/${endpoint}/${item.id}`, {
          withCredentials: true
        });
        fetchData();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleToggleActive = async (item) => {
    if (!canEdit || activeTab !== 'client-logos') return;
    
    try {
      await axios.put(`${SERVER}/content/admin/client-logos/${item.id}`, {
        ...item,
        active: !item.active
      }, {
        withCredentials: true
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setEditingItem(null);
    setModalType(activeTab);
    
    if (activeTab === 'stats') {
      setFormData({
        label: '',
        value: '',
        icon: '',
        order_index: 0
      });
    } else if (activeTab === 'milestones') {
      setFormData({
        year: new Date().getFullYear(),
        title: '',
        description: '',
        order_index: 0
      });
    } else if (activeTab === 'client-logos') {
      setFormData({
        name: '',
        logo: null,
        url: '',
        order_index: 0,
        active: true
      });
    }
    
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    switch (activeTab) {
      case 'stats':
        return renderStats();
      case 'milestones':
        return renderMilestones();
      case 'client-logos':
        return renderClientLogos();
      default:
        return null;
    }
  };

  const renderStats = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {stats.map((stat) => (
        <div key={stat.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {stat.icon && <i className={`${stat.icon} text-2xl text-blue-600`}></i>}
              <div>
                <h3 className="font-bold text-slate-800">{stat.label}</h3>
                <p className="text-2xl font-bold text-blue-600">{stat.value}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEdit(stat)}
                disabled={!canEdit}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => handleDelete(stat)}
                disabled={!canDelete}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaTrash />
              </button>
            </div>
          </div>
          <div className="text-sm text-slate-500">Order: {stat.order_index}</div>
        </div>
      ))}
    </div>
  );

  const renderMilestones = () => (
    <div className="space-y-4">
      {milestones.map((milestone) => (
        <div key={milestone.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{milestone.year}</span>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">{milestone.title}</h3>
                  <p className="text-slate-600">{milestone.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEdit(milestone)}
                disabled={!canEdit}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => handleDelete(milestone)}
                disabled={!canDelete}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaTrash />
              </button>
            </div>
          </div>
          <div className="text-sm text-slate-500 mt-2">Order: {milestone.order_index}</div>
        </div>
      ))}
    </div>
  );

  const renderClientLogos = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {clientLogos.map((logo) => (
        <div key={logo.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
          <div className="text-center mb-4">
            {logo.logo && (
              <img
                src={`${SERVER}${logo.logo}`}
                alt={logo.name}
                className="w-20 h-20 object-contain mx-auto mb-3 rounded-lg"
              />
            )}
            <h3 className="font-bold text-slate-800">{logo.name}</h3>
            {logo.url && (
              <a href={logo.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
                Visit Website
              </a>
            )}
          </div>
          
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => handleToggleActive(logo)}
              disabled={!canEdit}
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                logo.active
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            >
              {logo.active ? <FaToggleOn /> : <FaToggleOff />}
              {logo.active ? 'Active' : 'Inactive'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEdit(logo)}
                disabled={!canEdit}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => handleDelete(logo)}
                disabled={!canDelete}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <FaTrash />
              </button>
            </div>
          </div>
          <div className="text-sm text-slate-500">Order: {logo.order_index}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Content Blocks Management</h2>
          <p className="text-slate-600">Manage statistics, milestones, and client logos</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add {tabs.find(t => t.id === activeTab)?.label}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-2">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
              }`}
            >
              <span className="text-lg">{tab.icon}</span>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="animate-fadeIn">
        {renderContent()}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingItem ? 'Edit' : 'Add'} {tabs.find(t => t.id === modalType)?.label}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {modalType === 'stats' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Label *
                      </label>
                      <input
                        type="text"
                        value={formData.label || ''}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Projects Completed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Value *
                      </label>
                      <input
                        type="text"
                        value={formData.value || ''}
                        onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="150+"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Icon Class
                      </label>
                      <input
                        type="text"
                        value={formData.icon || ''}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="fa-solid fa-check-circle"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Order Index
                      </label>
                      <input
                        type="number"
                        value={formData.order_index || 0}
                        onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </>
              )}

              {modalType === 'milestones' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Year *
                      </label>
                      <input
                        type="number"
                        value={formData.year || new Date().getFullYear()}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() })}
                        required
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Order Index
                      </label>
                      <input
                        type="number"
                        value={formData.order_index || 0}
                        onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Company Founded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Brief description of the milestone"
                    />
                  </div>
                </>
              )}

              {modalType === 'client-logos' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Client Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Company Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Website URL
                      </label>
                      <input
                        type="url"
                        value={formData.url || ''}
                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://company.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Logo Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Order Index
                      </label>
                      <input
                        type="number"
                        value={formData.order_index || 0}
                        onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-semibold text-slate-700">Active</span>
                    </label>
                  </div>
                </>
              )}

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingItem ? 'Update' : 'Create'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentBlocks;